import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { ASSETS } from '../../config/assets';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  ArrowLeft, Users, FileText, Trash2, Plus, Shield, UserPlus,
  LogOut, ChevronRight, Calendar, Lock, Eye, EyeOff, Copy, Check
} from 'lucide-react';

const API = ''; // Relative paths — works on any domain (preview + production)

const apiFetch = async (path, options = {}) => {
  const res = await fetch(`${API}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Request failed');
  return data;
};

const MarcoAdmin = () => {
  const { user, logout } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [marcos, setMarcos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', name: '', role: 'user', unlimited: true, max_uses: '', password: '', confirm_password: '' });
  const [createError, setCreateError] = useState('');
  const [createdUser, setCreatedUser] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [copiedPw, setCopiedPw] = useState(false);

  const t = (en, fr) => language === 'fr' ? fr : en;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersData, marcosData] = await Promise.all([
        apiFetch('/api/auth/users'),
        apiFetch('/api/marcom/admin/all'),
      ]);
      setUsers(usersData);
      setMarcos(marcosData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateError('');
    try {
      const payload = {
        email: newUser.email,
        name: newUser.name,
        role: newUser.role,
        unlimited: newUser.unlimited,
        max_uses: newUser.unlimited ? null : parseInt(newUser.max_uses) || 10,
        password: newUser.password || null,
        confirm_password: newUser.confirm_password || null,
      };
      const result = await apiFetch('/api/auth/users', { method: 'POST', body: JSON.stringify(payload) });
      setCreatedUser(result);
      setShowCreate(false);
      setNewUser({ email: '', name: '', role: 'user', unlimited: true, max_uses: '', password: '', confirm_password: '' });
      loadData();
    } catch (err) {
      setCreateError(err.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await apiFetch(`/api/auth/users/${userId}`, { method: 'DELETE' });
      setUsers(prev => prev.filter(u => u.id !== userId));
      setDeleteId(null);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteMarco = async (marcoId) => {
    try {
      await apiFetch(`/api/marcom/${marcoId}`, { method: 'DELETE' });
      setMarcos(prev => prev.filter(m => m.id !== marcoId));
      setDeleteId(null);
    } catch (err) {
      console.error(err);
    }
  };

  const copyTempPassword = () => {
    navigator.clipboard.writeText(createdUser.temporary_password);
    setCopiedPw(true);
    setTimeout(() => setCopiedPw(false), 2000);
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  if (user?.role !== 'super_admin') {
    navigate('/marcom');
    return null;
  }

  return (
    <div data-testid="marco-admin" className="min-h-screen bg-[#0D0D0D]">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#0D0D0D]/95 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/marcom')} className="p-2 text-white/50 hover:text-white hover:bg-white/5 rounded-lg">
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <Link to="/" className="flex items-center gap-3">
              <img src={ASSETS.LOGO} alt="YUL On Tap" className="h-8 w-auto" />
            </Link>
            <div className="h-5 w-px bg-white/10" />
            <span className="font-heading text-lg font-semibold text-white">
              {t('Admin Panel', 'Panneau Admin')}
            </span>
          </div>
          <button onClick={() => { logout(); navigate('/portal'); }} className="p-2 text-white/40 hover:text-white rounded-lg">
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-1 mb-8 bg-[#1A1A1A] p-1 rounded-xl border border-white/10 w-fit">
          <button
            onClick={() => setTab('users')}
            data-testid="tab-users"
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              tab === 'users' ? 'bg-wine text-white' : 'text-white/50 hover:text-white hover:bg-white/5'
            }`}
          >
            <Users className="w-4 h-4" strokeWidth={1.5} />
            {t('Users', 'Utilisateurs')} ({users.length})
          </button>
          <button
            onClick={() => setTab('marcos')}
            data-testid="tab-marcos"
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              tab === 'marcos' ? 'bg-wine text-white' : 'text-white/50 hover:text-white hover:bg-white/5'
            }`}
          >
            <FileText className="w-4 h-4" strokeWidth={1.5} />
            {t('All Marcoms', 'Tous les Marcoms')} ({marcos.length})
          </button>
        </div>

        {/* Created User notification */}
        {createdUser && (
          <div data-testid="created-user-notice" className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
            <p className="text-green-400 text-sm font-medium mb-2">
              {t('User created successfully!', 'Utilisateur cree avec succes!')}
            </p>
            <div className="flex items-center gap-3 text-sm">
              <span className="text-white/70">{createdUser.email}</span>
              <span className="text-white/30">|</span>
              <span className="text-white/50">{t('Temp password:', 'Mot de passe temp:')}</span>
              <code className="text-white font-mono bg-white/10 px-2 py-0.5 rounded">{createdUser.temporary_password}</code>
              <button onClick={copyTempPassword} className="text-white/40 hover:text-white">
                {copiedPw ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" strokeWidth={1.5} />}
              </button>
            </div>
            <button onClick={() => setCreatedUser(null)} className="text-white/30 hover:text-white text-xs mt-2">
              {t('Dismiss', 'Fermer')}
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-wine border-t-transparent rounded-full animate-spin" />
          </div>
        ) : tab === 'users' ? (
          /* Users Tab */
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl text-white">{t('User Management', 'Gestion des Utilisateurs')}</h2>
              <Button
                onClick={() => setShowCreate(!showCreate)}
                data-testid="create-user-btn"
                className="bg-wine hover:bg-wine-light text-white rounded-xl h-10 text-sm"
              >
                <UserPlus className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                {t('Add User', 'Ajouter')}
              </Button>
            </div>

            {/* Create User Form */}
            {showCreate && (
              <form onSubmit={handleCreateUser} data-testid="create-user-form" className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 mb-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-white/60 text-xs font-medium block mb-1">{t('Name', 'Nom')}</label>
                    <Input
                      value={newUser.name}
                      onChange={(e) => setNewUser(p => ({ ...p, name: e.target.value }))}
                      data-testid="new-user-name"
                      className="bg-white/5 border-white/10 text-white h-10 rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-white/60 text-xs font-medium block mb-1">{t('Email', 'Courriel')}</label>
                    <Input
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser(p => ({ ...p, email: e.target.value }))}
                      data-testid="new-user-email"
                      className="bg-white/5 border-white/10 text-white h-10 rounded-lg"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="text-white/60 text-xs font-medium block mb-1">{t('Role', 'Role')}</label>
                    <select
                      value={newUser.role}
                      onChange={(e) => setNewUser(p => ({ ...p, role: e.target.value }))}
                      data-testid="new-user-role"
                      className="w-full bg-white/5 border border-white/10 text-white h-10 rounded-lg px-3 text-sm"
                    >
                      <option value="user">User</option>
                      <option value="executive">Executive</option>
                      <option value="super_admin">Super Admin</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-white/60 text-xs font-medium block mb-1">{t('Usage', 'Utilisation')}</label>
                    <select
                      value={newUser.unlimited ? 'unlimited' : 'limited'}
                      onChange={(e) => setNewUser(p => ({ ...p, unlimited: e.target.value === 'unlimited' }))}
                      data-testid="new-user-usage"
                      className="w-full bg-white/5 border border-white/10 text-white h-10 rounded-lg px-3 text-sm"
                    >
                      <option value="unlimited">{t('Unlimited', 'Illimite')}</option>
                      <option value="limited">{t('Limited', 'Limite')}</option>
                    </select>
                  </div>
                  {!newUser.unlimited && (
                    <div>
                      <label className="text-white/60 text-xs font-medium block mb-1">{t('Max Uses', 'Utilisations Max')}</label>
                      <Input
                        type="number"
                        value={newUser.max_uses}
                        onChange={(e) => setNewUser(p => ({ ...p, max_uses: e.target.value }))}
                        data-testid="new-user-max-uses"
                        className="bg-white/5 border-white/10 text-white h-10 rounded-lg"
                        min="1"
                      />
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-white/60 text-xs font-medium block mb-1">{t('Initial Password', 'Mot de Passe Initial')} <span className="text-white/25">({t('optional', 'optionnel')})</span></label>
                    <Input
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser(p => ({ ...p, password: e.target.value }))}
                      data-testid="new-user-password"
                      placeholder={t('Leave empty for auto-generated', 'Vide = auto-genere')}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/20 h-10 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="text-white/60 text-xs font-medium block mb-1">{t('Confirm Password', 'Confirmer le Mot de Passe')}</label>
                    <Input
                      type="password"
                      value={newUser.confirm_password}
                      onChange={(e) => setNewUser(p => ({ ...p, confirm_password: e.target.value }))}
                      data-testid="new-user-confirm-password"
                      placeholder={t('Confirm password', 'Confirmer')}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/20 h-10 rounded-lg"
                    />
                  </div>
                </div>
                {createError && <p className="text-red-400 text-sm">{createError}</p>}
                <div className="flex gap-3">
                  <Button type="submit" data-testid="submit-create-user" className="bg-wine hover:bg-wine-light text-white rounded-lg h-10 text-sm">
                    {t('Create User', 'Creer Utilisateur')}
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setShowCreate(false)} className="text-white/50 h-10 text-sm">
                    {t('Cancel', 'Annuler')}
                  </Button>
                </div>
              </form>
            )}

            {/* Users list */}
            <div className="space-y-2">
              {users.map((u) => (
                <div key={u.id} data-testid={`user-row-${u.id}`} className="bg-[#1A1A1A] border border-white/10 rounded-xl p-4 flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    u.role === 'super_admin' ? 'bg-wine/20' : u.role === 'executive' ? 'bg-blue-500/20' : 'bg-white/5'
                  }`}>
                    {u.role === 'super_admin' ? (
                      <Shield className="w-5 h-5 text-wine" strokeWidth={1.5} />
                    ) : u.role === 'executive' ? (
                      <Shield className="w-5 h-5 text-blue-400" strokeWidth={1.5} />
                    ) : (
                      <Users className="w-5 h-5 text-white/40" strokeWidth={1.5} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium text-sm">{u.name}</span>
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                        u.role === 'super_admin' ? 'bg-wine/10 text-wine border-wine/20' :
                        u.role === 'executive' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' :
                        'bg-white/5 text-white/40 border-white/10'
                      }`}>
                        {u.role === 'super_admin' ? 'Admin' : u.role === 'executive' ? 'Executive' : 'User'}
                      </span>
                    </div>
                    <span className="text-white/40 text-xs">{u.email}</span>
                  </div>
                  <div className="text-right hidden sm:block">
                    <span className="text-white/30 text-xs">
                      {u.permissions?.unlimited ? t('Unlimited', 'Illimite') : `${u.permissions?.uses_remaining || 0} ${t('remaining', 'restants')}`}
                    </span>
                  </div>
                  {u.role !== 'super_admin' && (
                    deleteId === `user-${u.id}` ? (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleDeleteUser(u.id)} className="bg-red-600 text-white text-xs h-8 rounded-lg">
                          {t('Delete', 'Supprimer')}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setDeleteId(null)} className="text-white/50 text-xs h-8">
                          {t('Cancel', 'Annuler')}
                        </Button>
                      </div>
                    ) : (
                      <button onClick={() => setDeleteId(`user-${u.id}`)} className="p-2 text-white/20 hover:text-red-400 rounded-lg">
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    )
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* Marcos Tab */
          <div>
            <h2 className="font-heading text-xl text-white mb-4">{t('All Marcoms', 'Tous les Marcoms')}</h2>
            {marcos.length === 0 ? (
              <p className="text-white/40 text-center py-12">{t('No Marcos created yet.', 'Aucun Marco cree.')}</p>
            ) : (
              <div className="space-y-2">
                {marcos.map((m) => (
                  <div key={m.id} data-testid={`admin-marco-${m.id}`} className="bg-[#1A1A1A] border border-white/10 rounded-xl p-4 flex items-center gap-4">
                    <div className="w-10 h-10 bg-wine/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-wine" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-white font-medium text-sm block truncate">{m.name}</span>
                      <div className="flex items-center gap-2 text-xs text-white/40 mt-0.5">
                        <span>{m.created_by_name || m.created_by_email}</span>
                        <span className="text-white/20">|</span>
                        <span>{m.page_count} {t('pages', 'pages')}</span>
                        <span className="text-white/20">|</span>
                        <span>{formatDate(m.created_at)}</span>
                      </div>
                    </div>
                    {deleteId === `marco-${m.id}` ? (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleDeleteMarco(m.id)} className="bg-red-600 text-white text-xs h-8 rounded-lg">
                          {t('Delete', 'Supprimer')}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setDeleteId(null)} className="text-white/50 text-xs h-8">
                          {t('Cancel', 'Annuler')}
                        </Button>
                      </div>
                    ) : (
                      <button onClick={() => setDeleteId(`marco-${m.id}`)} className="p-2 text-white/20 hover:text-red-400 rounded-lg">
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default MarcoAdmin;
